/* Universal 01 presentation of native Shopify rich text. Content stays editable. */
(() => {
  'use strict';
  if (window.BPLUniversalAccordions) {
    window.BPLUniversalAccordions.init(document);
    return;
  }
  const selector = '.bpl-product-content--01 .bpl-universal-content [data-bpl-field-rows]';
  const inlineTags = new Set(['SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'SMALL', 'SUB', 'SUP', 'A', 'BR']);
  const meaningful = node => node.nodeType !== 3 || Boolean(node.data.trim());
  const children = node => Array.from(node.childNodes).filter(meaningful);
  const continuationTitle = title => /^(?:notes?|warnings?|for example|for instance|examples?|e\.g\.|i\.e\.|please note|important|remember|tips?):$/iu.test(title);

  function safeInline(node) {
    return node.nodeType === 3 || (node.nodeType === 1 && inlineTags.has(node.tagName)
      && !node.id && !node.hasAttribute('contenteditable')
      && Array.from(node.childNodes).every(safeInline));
  }

  function rowPlan(row, dimensions, selectedNodes) {
    if (row.hasAttribute('contenteditable')) return null;
    const parts = children(row);
    // Shopify may put a paragraph inside a list item. Keep both original nodes.
    const host = row.tagName === 'LI' && parts.length === 1 && parts[0].nodeType === 1 && parts[0].tagName === 'P'
      ? parts[0] : row;
    if (host.hasAttribute('contenteditable')) return null;
    const nodes = selectedNodes || Array.from(host.childNodes);
    if (!nodes.length || nodes.some(node => !safeInline(node))) return null;
    const text = nodes.map(node => node.textContent).join('');
    const colon = text.indexOf(':');
    const title = text.slice(0, colon + 1).trim();
    if (colon < 1 || !/^[^:|.!?;]{1,80}:$/u.test(title) || !/\p{L}/u.test(title)
      || title.split(/\s+/u).length > 10 || !text.slice(colon + 1).trim()
      || /^(?:https?|ftp|mailto|tel|data|urn|file|javascript):$/iu.test(title) || continuationTitle(title)
      || text.slice(colon + 1, colon + 3) === '//'
      || (/\d/u.test(text[colon - 1]) && /\d/u.test(text[colon + 1] || ''))) return null;
    // The comparison helper runs first. Remaining labeled rows preserve their
    // literal values, including pipes, in the two-column presentation.
    let offset = 0;
    let boundary = null;
    for (const node of nodes) {
      const length = node.textContent.length;
      if (offset <= colon) {
        if (node.nodeType === 1 && (node.matches('a, br') || node.querySelector('a, br'))) return null;
        if (offset + length >= colon + 1) {
          const splitAt = colon + 1 - offset;
          // A formatted element spanning both columns would have to be cloned.
          // Leave that unsupported structure untouched instead.
          if (node.nodeType !== 3 && node.textContent.slice(splitAt).trim()) return null;
          boundary = { node, splitAt: node.nodeType === 3 ? splitAt : null };
          break;
        }
      }
      offset += length;
    }
    if (!boundary) return null;
    return { row, host, title, boundary, nodes };
  }

  function formattedLabel(item) {
    let formatted = false;
    for (const node of item.nodes) {
      if (node.nodeType === 3) {
        const prefix = node === item.boundary.node ? node.data.slice(0, item.boundary.splitAt) : node.data;
        if (prefix.trim() && prefix.trim() !== ':') return false;
      } else if (node.classList.contains('bpl-detail-label') || node.querySelector('.bpl-detail-label')
        || node.matches('strong, b') || (node.tagName === 'SPAN' && node.textContent.trim().endsWith(':'))) {
        formatted = true;
      }
      if (node === item.boundary.node) return formatted;
    }
    return false;
  }

  function rowSegments(row, dimensions) {
    const whole = rowPlan(row, dimensions);
    if (!whole) return null;
    // Comparison/single-specification ownership stays unchanged.
    if (dimensions) return { ...whole, segments: [whole] };
    const nodes = whole.nodes;
    const segments = [];
    let start = 0;
    for (let index = 0; index < nodes.length; index++) {
      if (nodes[index].nodeType !== 1 || nodes[index].tagName !== 'BR') continue;
      let end = index;
      let breaks = 0;
      while (end < nodes.length && ((nodes[end].nodeType === 1 && nodes[end].tagName === 'BR')
        || (nodes[end].nodeType === 3 && !nodes[end].data.trim()))) {
        if (nodes[end].nodeType === 1) breaks++;
        end++;
      }
      // A visible line break plus an independently formatted field label is
      // required. Newline text alone and ordinary body colons never split.
      if (breaks >= 1 && end < nodes.length) {
        const following = rowPlan(row, false, nodes.slice(end));
        const followingText = nodes.slice(end).map(node => node.textContent).join('');
        const followsAnnotation = continuationTitle(followingText.slice(0, followingText.indexOf(':') + 1).trim());
        const next = nodes[end];
        const explicit = next.nodeType === 1 && (next.classList.contains('bpl-detail-label') || next.querySelector('.bpl-detail-label'));
        // The existing highlighter also marks short prose lead-ins; those
        // remain body content, including their original visible BRs.
        if (explicit && !following && !followsAnnotation) return null;
        if (following && formattedLabel(following)) {
          const segment = rowPlan(row, false, nodes.slice(start, index));
          if (!segment) return null;
          segment.separator = nodes.slice(index, end);
          segments.push(segment);
          start = end;
        }
      }
      index = end - 1;
    }
    const last = rowPlan(row, false, nodes.slice(start));
    if (!last) return null;
    segments.push(last);
    return { ...whole, segments };
  }

  function plan(body) {
    if (body.dataset.bplRowsEnhanced === 'true' || body.querySelector('table, [contenteditable]')) return null;
    let container = body;
    const outer = children(body);
    if (outer.length === 1 && outer[0].nodeType === 1 && outer[0].classList.contains('metafield-rich_text_field')) container = outer[0];
    const nodes = children(container);
    if (!nodes.length || nodes.some(node => node.nodeType !== 1)) return null;
    const dimensions = body.dataset.bplFieldRows === 'dimensions';
    const list = nodes[0].tagName === 'UL' ? nodes[0] : null;
    let rows;
    let blankParagraphs = [];
    if (list) {
      if (list.hasAttribute('contenteditable') || children(list).some(node => node.nodeType !== 1 || node.tagName !== 'LI')) return null;
      if ((!dimensions && nodes.length !== 1) || (dimensions && nodes.slice(1).some(node => node.tagName !== 'P'))) return null;
      rows = Array.from(list.children);
    } else {
      if (nodes.some(node => node.tagName !== 'P')) return null;
      // A sizing note follows the data rather than becoming another table row.
      const noteAt = dimensions ? nodes.findIndex(node => /^\s*Notes?:/iu.test(node.textContent)) : -1;
      rows = noteAt < 0 ? nodes : nodes.slice(0, noteAt);
      if (!dimensions) {
        const meaningfulEmpty = 'a, [tabindex], [role], [aria-label], [aria-labelledby], [aria-describedby], [title], [onclick], [onkeydown]';
        blankParagraphs = rows.filter(row => !row.id && !row.hasAttribute('contenteditable')
          && !row.matches(meaningfulEmpty) && !row.querySelector(meaningfulEmpty)
          && !row.textContent.trim() && Array.from(row.childNodes).every(safeInline));
        rows = rows.filter(row => !blankParagraphs.includes(row));
      }
    }
    if (!rows.length || rows.length > 16 || (dimensions && rows.length < 2)) return null;
    const planned = rows.map(row => rowSegments(row, dimensions));
    if (planned.some(row => !row)) return null;
    const segments = planned.flatMap(item => item.segments);
    if (segments.length > 16 || new Set(segments.map(item => item.title.toLowerCase())).size !== segments.length) return null;
    // Converting native P/UL rows to a table must not discard merchant attributes.
    if (dimensions && ((list && list.attributes.length) || planned.some(item => item.row.attributes.length || (item.host !== item.row && item.host.attributes.length)))) return null;
    return { body, container, list, rows: planned, dimensions, blankParagraphs };
  }

  function splitRow(item) {
    const { host, boundary } = item;
    const nodes = item.nodes.slice();
    if (boundary.splitAt !== null && boundary.splitAt < boundary.node.data.length) {
      const tail = boundary.node.splitText(boundary.splitAt);
      nodes.splice(nodes.indexOf(boundary.node) + 1, 0, tail);
    }
    const label = host.ownerDocument.createElement('span');
    const value = host.ownerDocument.createElement('span');
    label.className = 'bpl-field-label';
    value.className = 'bpl-field-value';
    let after = false;
    nodes.forEach(node => {
      (after ? value : label).appendChild(node);
      if (node === boundary.node) after = true;
    });
    return { label, value };
  }

  function apply(group) {
    const document = group.body.ownerDocument;
    if (group.dimensions) {
      const table = document.createElement('table');
      table.className = 'bpl-specifications bpl-specifications--single bpl-field-specifications';
      table.setAttribute('data-bpl-specifications', '');
      table.setAttribute('role', 'table');
      table.setAttribute('aria-label', 'Product dimensions');
      const tbody = document.createElement('tbody');
      tbody.setAttribute('role', 'rowgroup');
      group.rows.forEach(item => {
        const parts = splitRow(item);
        const tr = document.createElement('tr');
        const th = document.createElement('th');
        const td = document.createElement('td');
        tr.setAttribute('role', 'row');
        th.scope = 'row';
        th.setAttribute('role', 'rowheader');
        td.setAttribute('role', 'cell');
        th.appendChild(parts.label);
        td.appendChild(parts.value);
        tr.append(th, td);
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      const anchor = group.list || group.rows[0].row;
      anchor.parentNode.insertBefore(table, anchor);
      if (group.list) group.list.remove();
      else group.rows.forEach(item => item.row.remove());
    } else {
      let wrapper = group.list;
      if (!wrapper) {
        const sourceNodes = Array.from(group.container.childNodes);
        wrapper = document.createElement('div');
        group.container.insertBefore(wrapper, group.container.firstChild);
        sourceNodes.forEach(node => wrapper.appendChild(node));
      }
      wrapper.classList.add('bpl-field-rows');
      group.blankParagraphs.forEach(paragraph => {
        const separator = document.createElement('div');
        separator.className = 'bpl-field-separator';
        separator.hidden = true;
        paragraph.parentNode.insertBefore(separator, paragraph);
        separator.appendChild(paragraph);
      });
      group.rows.forEach((item, rowIndex) => {
        if (item.host !== item.row) item.row.classList.add('bpl-field-list-item');
        if (item.segments.length === 1) {
          const parts = splitRow(item.segments[0]);
          item.host.classList.add('bpl-field-row');
          if (rowIndex === group.rows.length - 1) item.host.classList.add('bpl-field-row--last');
          item.host.append(parts.label, parts.value);
          return;
        }
        // Retain the original P/LI and all its attributes. New span rows are
        // valid paragraph contents; no original element is cloned or removed.
        item.host.classList.add('bpl-field-row-group');
        item.segments.forEach((segment, segmentIndex) => {
          const parts = splitRow(segment);
          const row = document.createElement('span');
          row.className = 'bpl-field-row';
          if (rowIndex === group.rows.length - 1 && segmentIndex === item.segments.length - 1) row.classList.add('bpl-field-row--last');
          row.append(parts.label, parts.value);
          item.host.appendChild(row);
          if (segment.separator) {
            const separator = document.createElement('span');
            separator.className = 'bpl-field-separator';
            separator.hidden = true;
            segment.separator.forEach(node => separator.appendChild(node));
            item.host.appendChild(separator);
          }
        });
      });
    }
    group.body.dataset.bplRowsEnhanced = 'true';
  }

  function init(root = document) {
    // The existing helpers remain the single owners of comparisons and color.
    if (window.BPLProductSpecifications) window.BPLProductSpecifications.init(root);
    if (window.BPLAccordionHighlights) window.BPLAccordionHighlights.init(root);
    const bodies = [];
    if (root.matches && root.matches(selector)) bodies.push(root);
    if (root.querySelectorAll) bodies.push(...root.querySelectorAll(selector));
    bodies.forEach(body => { const candidate = plan(body); if (candidate) apply(candidate); });
  }
  window.BPLUniversalAccordions = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
